// Copyright ©️ 2026 eVolve MEP, LLC

import React, { Fragment, useMemo, useState } from 'react';

import { Popover, ActionIcon, Text, Stack, Checkbox, Space, Flex, Title, Indicator, Button } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import type { SpoolRequestEntry } from 'modules/Shop/Spooling/Assigning/useSpoolRequestsLoader';

import { type FilterKey, filterNoneKey, type FilterNoneKey, type SpoolFilter } from '../hooks/useSpoolFilters';

type Props = {
  spools: readonly RenderableSpool[];
  requestedSpools?: SpoolRequestEntry[] | null;
  spoolFilter: SpoolFilter;
  onFilterChanged: (filter: SpoolFilter) => void;
};

const filterTitles = {
  spoolStatus: 'Spool Status',
  package: 'Package',
  level: 'Level',
  packageStatus: 'Package Status',
  location: 'Location',
} as const satisfies Record<FilterKey, string>;

export const SpoolFiltersPopover = ({ spools, requestedSpools, spoolFilter, onFilterChanged }: Props) => {
  const [opened, setOpened] = useState(false);
  const possibleFilters = useMemo<SpoolFilter>(
    () =>
      spools
        .concat((requestedSpools ?? []).map(([_, spool]) => spool))
        .flatMap((spool) => strongEntries(spool))
        .reduce((out, [key, value]) => {
          if (typeof value !== 'string' || key === 'name' || key === 'objects') return out;
          if (isNotNil(out[key])) {
            if (!out[key].includes(value)) {
              out[key].push(value);
            }
          } else {
            out[key] = [value];
          }
          return out;
        }, {} as SpoolFilter),
    [spools, requestedSpools],
  );

  const filtersAreSet = Object.values(spoolFilter).some((v) => !!v.length);

  const orderedFilters = useMemo(
    () => strongEntries(possibleFilters).toSorted((a, b) => a[0].localeCompare(b[0])),
    [possibleFilters],
  );

  const onChange = (key: FilterKey, val: string | FilterNoneKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      onFilterChanged({
        ...spoolFilter,
        [key]: [...(spoolFilter[key] ?? []), val],
      });
    } else {
      onFilterChanged({
        ...spoolFilter,
        [key]: spoolFilter[key]?.filter((v) => v !== val),
      });
    }
  };

  return (
    <Popover onChange={setOpened} opened={opened}>
      <Popover.Target>
        <Indicator disabled={!filtersAreSet}>
          <AgGridStyleTooltip label="Spool filters" withArrow openDelay={200} hidden={opened}>
            <Button size="xs" variant="light" onClick={() => setOpened((o) => !o)} p={2}>
              <EvolveIcon icon="Filter" />
            </Button>
          </AgGridStyleTooltip>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown mah={400} miw={180} display="flex">
        <Stack flex={1} style={{ overflowY: 'auto' }} gap={4}>
          <Flex align="center" gap="xl" mb={4}>
            <Title order={5}>Spool filters</Title>
            <AgGridStyleTooltip label="Clear filters" withArrow openDelay={200} hidden={!filtersAreSet}>
              <ActionIcon
                onClick={() => onFilterChanged({})}
                variant="light"
                size="sm"
                style={{
                  opacity: filtersAreSet ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                  cursor: filtersAreSet ? 'pointer' : 'default',
                }}
              >
                <EvolveIcon icon="ClearFilters" size="sm" />
              </ActionIcon>
            </AgGridStyleTooltip>
          </Flex>
          {orderedFilters.map(([key, values]) => (
            <Fragment key={key}>
              <Text fz="sm" fw={spoolFilter[key]?.some((v) => values?.includes(v)) ? 'bold' : undefined}>
                {filterTitles[key]}
              </Text>
              {values
                ?.toSorted((a, b) => a.localeCompare(b))
                .map((val) => (
                  <Checkbox
                    key={`${key}-${val}`}
                    ml={4}
                    label={val}
                    checked={spoolFilter[key]?.includes(val) ?? false}
                    onChange={onChange(key, val)}
                    size="xs"
                  />
                ))}
              <Checkbox
                ml={4}
                label="(empty)"
                c="gray"
                fs="italic"
                checked={spoolFilter[key]?.includes(filterNoneKey) ?? false}
                onChange={onChange(key, filterNoneKey)}
                size="xs"
              />
              <Space />
            </Fragment>
          ))}
          {orderedFilters.length === 0 && (
            <Text fz="sm" c="dimmed">
              No filters available.
            </Text>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
